import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight } from "lucide-react";
import { API } from "@/lib/constants";

const LABELS = {
  en: { checking: "Confirming your payment…", success: "Welcome to Premium!", success_sub: "Unlimited CVs unlocked. Enjoy 🎉", pack_success: "Pack added to your account!", pack_success_sub: (n) => `${n} CV${n > 1 ? "s" : ""} credited. Ready to optimize 🎉`, cta: "Start using Premium", pack_cta: "Create my next CV", failed: "Payment not completed", failed_sub: "No worries — try again anytime.", back: "Back to home" },
  fr: { checking: "Confirmation de ton paiement…", success: "Bienvenue en Premium !", success_sub: "CV illimités débloqués. Profites-en 🎉", pack_success: "Pack ajouté à ton compte !", pack_success_sub: (n) => `${n} CV${n > 1 ? "s" : ""} crédité${n > 1 ? "s" : ""}. À toi de jouer 🎉`, cta: "Utiliser Premium", pack_cta: "Créer mon prochain CV", failed: "Paiement non finalisé", failed_sub: "Pas de souci — tu peux réessayer.", back: "Retour à l'accueil" },
  de: { checking: "Zahlung wird bestätigt…", success: "Willkommen bei Premium!", success_sub: "Unbegrenzte CVs freigeschaltet 🎉", pack_success: "Paket zu deinem Konto hinzugefügt!", pack_success_sub: (n) => `${n} CV${n > 1 ? "s" : ""} gutgeschrieben 🎉`, cta: "Premium starten", pack_cta: "Neuen CV erstellen", failed: "Zahlung nicht abgeschlossen", failed_sub: "Kein Problem — versuche es erneut.", back: "Zur Startseite" },
  it: { checking: "Conferma del pagamento…", success: "Benvenuto in Premium!", success_sub: "CV illimitati sbloccati 🎉", pack_success: "Pacchetto aggiunto al tuo account!", pack_success_sub: (n) => `${n} CV accreditat${n > 1 ? "i" : "o"} 🎉`, cta: "Usa Premium", pack_cta: "Crea il mio prossimo CV", failed: "Pagamento non completato", failed_sub: "Nessun problema — riprova.", back: "Torna alla home" },
  es: { checking: "Confirmando tu pago…", success: "¡Bienvenido a Premium!", success_sub: "CV ilimitados desbloqueados 🎉", pack_success: "¡Pack añadido a tu cuenta!", pack_success_sub: (n) => `${n} CV${n > 1 ? "s" : ""} acreditado${n > 1 ? "s" : ""} 🎉`, cta: "Usar Premium", pack_cta: "Crear mi próximo CV", failed: "Pago no completado", failed_sub: "No hay problema — inténtalo de nuevo.", back: "Volver al inicio" },
  pt: { checking: "A confirmar o pagamento…", success: "Bem-vindo ao Premium!", success_sub: "CVs ilimitados desbloqueados 🎉", pack_success: "Pack adicionado à tua conta!", pack_success_sub: (n) => `${n} CV${n > 1 ? "s" : ""} creditado${n > 1 ? "s" : ""} 🎉`, cta: "Usar Premium", pack_cta: "Criar o meu próximo CV", failed: "Pagamento não concluído", failed_sub: "Sem problema — tenta novamente.", back: "Voltar ao início" },
};

export default function PaymentSuccessPage({ lang = "en", onDone }) {
  const t = LABELS[lang] || LABELS.en;
  const [state, setState] = useState("checking"); // checking | success | failed
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState("premium"); // "pack" | "premium"
  const [cvsAdded, setCvsAdded] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setKind(r.data.kind || "premium");
          setCvsAdded(r.data.cvs_added || 0);
          setState("success");
          return;
        }
        if (r.data.status === "expired" || r.data.status === "failed") {
          setState("failed");
          setMsg(r.data.payment_status || r.data.status);
          return;
        }
      } catch (e) {
        if (attempts >= 10) {
          setState("failed");
          setMsg("Timeout");
          return;
        }
      }
      if (attempts >= 15) {
        setState("failed");
        setMsg("Timeout — please contact support if you were charged");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const isPack = kind === "pack";
  const successTitle = isPack ? t.pack_success : t.success;
  const successSub = isPack ? t.pack_success_sub(cvsAdded) : t.success_sub;
  const successCta = isPack ? t.pack_cta : t.cta;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0] p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#EAE5DB] p-8 md:p-10 text-center shadow-xl">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#D95D39] mx-auto animate-spin mb-5" />
            <div className="font-heading font-bold text-xl text-[#292524]">{t.checking}</div>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-20 h-20 rounded-3xl bg-[#3B8268]/15 mx-auto mb-5 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#3B8268]" />
            </div>
            <div className="font-heading font-extrabold text-3xl text-[#292524] mb-2">{successTitle}</div>
            <p className="text-[#57534E] mb-6">{successSub}</p>
            <button onClick={onDone} className="btn-primary w-full" data-testid="payment-success-cta">
              <Sparkles className="w-4 h-4" /> {successCta} <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <div className="w-20 h-20 rounded-3xl bg-[#D9423E]/15 mx-auto mb-5 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-[#D9423E]" />
            </div>
            <div className="font-heading font-extrabold text-2xl text-[#292524] mb-2">{t.failed}</div>
            <p className="text-[#57534E] mb-2">{t.failed_sub}</p>
            {msg && <p className="text-xs text-[#78716C] mb-4">{msg}</p>}
            <button onClick={onDone} className="btn-outline w-full" data-testid="payment-failed-cta">
              {t.back}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
